import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import Loader from 'components/Loader';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { saveSection } from 'components/section-edit/saveSection';
import { ReactNode } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetJobRoleByIdQuery, useUpdateJobRoleMutation } from 'store/api/jobRoles';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { JobRoleDetailsDto, JobRoleForm } from 'types/jobRoles';
import { useClassifiers } from 'utils/useClassifiers';
import {
    JOB_ROLE_SECTION_FIELDS,
    JobRoleSectionId,
    jobRoleDtoToForm,
    jobRoleFormToRequest,
} from '../jobRoleFormMapping';
import { JobRoleFields, JobRoleSettingsFields, JobRoleTranslationsFields } from './JobRoleFields';
import { JOB_ROLE_FORM_CLASSIFIERS, JobRoleFormState, useJobRoleForm } from './useJobRoleForm';

const SECTION_FIELDS: Record<JobRoleSectionId, (form: JobRoleFormState) => ReactNode> = {
    'job-role': (form) => <JobRoleFields form={form} />,
    settings: (form) => <JobRoleSettingsFields form={form} />,
    translations: (form) => <JobRoleTranslationsFields form={form} />,
    'custom-fields': (form) => (
        <CustomFields control={form.methods.control as unknown as Control<FieldValues>} fields={form.customFields} />
    ),
};

interface SectionFormProps {
    section: JobRoleSectionId;
    initialData: JobRoleForm;
    customFields: CustomField[] | undefined;
    classifiers: Record<string, ClassifierData>;
    onClose: () => void;
}

function JobRoleSectionForm({ section, initialData, customFields, classifiers, onClose }: SectionFormProps) {
    // The page's own query argument, so the refetch refreshes the job role the page shows.
    const { id: jobRoleId = '' } = useParams();
    const { refetch } = useGetJobRoleByIdQuery(jobRoleId);
    const [updateJobRole] = useUpdateJobRoleMutation();
    const form = useJobRoleForm({ initialData, classifiers, entityCustomFields: customFields });

    const onSave = (values: JobRoleForm) =>
        saveSection(values, {
            section,
            sectionFields: JOB_ROLE_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (jobRole) => ({
                ...jobRoleDtoToForm(jobRole, classifiers),
                ...form.customFieldValuesOf(jobRole.customFields),
            }),
            toRequest: (edited) =>
                jobRoleFormToRequest(edited, form.mapCustomFieldsToDto(edited as unknown as Record<string, unknown>)),
            update: (request) => updateJobRole({ ...request, id: jobRoleId }).unwrap(),
            onSaved: onClose,
        });

    if (form.isLoadingCustomFieldData) {
        return <Loader />;
    }
    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose}>
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: JobRoleSectionId;
    jobRole: JobRoleDetailsDto;
    onClose: () => void;
}

function JobRoleSectionEditor({ section, jobRole, onClose }: EditorProps) {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: JOB_ROLE_FORM_CLASSIFIERS });

    // Custom fields frozen too: the form takes their values from them.
    const frozen = useFrozenOnceLoaded(!isLoadingClassifiers, () => ({
        initialData: jobRoleDtoToForm(jobRole, classifiers),
        customFields: jobRole.customFields,
    }));

    if (!frozen) {
        return <Loader />;
    }
    return (
        <JobRoleSectionForm
            section={section}
            initialData={frozen.initialData}
            customFields={frozen.customFields}
            classifiers={classifiers}
            onClose={onClose}
        />
    );
}

interface JobRoleSectionProps {
    id: JobRoleSectionId;
    titleId: string;
    jobRole: JobRoleDetailsDto;
    /** The read view, handed the card's Edit control. */
    children: (edit: ReactNode) => ReactNode;
}

/** A card of the job role page that edits in place with the job role form's fields for that card. */
export function JobRoleSection({ id, titleId, jobRole, children }: JobRoleSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => <JobRoleSectionEditor section={id} jobRole={jobRole} onClose={close} />}
        >
            {children}
        </SectionEdit>
    );
}
